import type { DayOffPriority, DraftAssignment } from '../../types.js'

/** One date of the month grid; `inMonth` false for the days of the weeks either side that fill the first and last rows. */
export interface WorkerMonthDay {
  date: string
  dayOfMonth: number
  inMonth: boolean
  assignment: DraftAssignment | null
  pto: boolean
  dayOff: DayOffPriority | null
  /** The open seat's own day. */
  seat: boolean
  /** Part of the run of days in a row the seat would join. */
  inRun: boolean
}

export interface WorkerMonthWeek {
  days: WorkerMonthDay[]
  /** Hours scheduled in this Sunday-to-Saturday week, counting only shifts on the board. */
  hours: number
}

export interface WorkerMonthInput {
  /** `YYYY-MM`. */
  period: string
  assignments: readonly DraftAssignment[]
  ptoDates: readonly string[]
  daysOff: readonly { date: string; priority: DayOffPriority }[]
  /** The open seat's date, inside `period`. */
  seatDate: string
}

const DAY_MS = 86_400_000

function dateOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

function msOf(date: string): number {
  return Date.parse(`${date}T00:00:00Z`)
}

/** The run of consecutive working days the seat's date would join, the seat included; empty when it would stand alone. */
function runAround(seatDate: string, worked: ReadonlySet<string>): Set<string> {
  const run = new Set([seatDate])
  for (let ms = msOf(seatDate) - DAY_MS; worked.has(dateOf(ms)); ms -= DAY_MS) {
    run.add(dateOf(ms))
  }
  for (let ms = msOf(seatDate) + DAY_MS; worked.has(dateOf(ms)); ms += DAY_MS) {
    run.add(dateOf(ms))
  }
  return run.size > 1 ? run : new Set()
}

/** The worker's month as Sunday-to-Saturday weeks: their shifts, PTO and days asked off, and where the open seat lands. */
export function buildWorkerMonth(input: WorkerMonthInput): WorkerMonthWeek[] {
  const byDate = new Map(input.assignments.map((entry) => [entry.date, entry]))
  const pto = new Set(input.ptoDates)
  const daysOff = new Map(input.daysOff.map((entry) => [entry.date, entry.priority]))
  const run = runAround(input.seatDate, new Set(byDate.keys()))
  const first = msOf(`${input.period}-01`)
  const [year, month] = input.period.split('-').map(Number) as [number, number]
  const last = Date.UTC(year, month, 0)
  const start = first - new Date(first).getUTCDay() * DAY_MS
  const end = last + (6 - new Date(last).getUTCDay()) * DAY_MS

  const weeks: WorkerMonthWeek[] = []
  for (let weekStart = start; weekStart <= end; weekStart += 7 * DAY_MS) {
    const days = Array.from({ length: 7 }, (_, index): WorkerMonthDay => {
      const date = dateOf(weekStart + index * DAY_MS)
      const inMonth = date.startsWith(input.period)
      return {
        date,
        dayOfMonth: Number(date.slice(8, 10)),
        inMonth,
        assignment: byDate.get(date) ?? null,
        pto: pto.has(date),
        dayOff: daysOff.get(date) ?? null,
        seat: date === input.seatDate,
        inRun: inMonth && run.has(date),
      }
    })
    weeks.push({ days, hours: days.reduce((sum, day) => sum + (day.assignment?.hours ?? 0), 0) })
  }
  return weeks
}
